import {
  Button,
  Css,
  GridColumn,
  GridTableLayout,
  IconButton,
  PageHeaderLayout,
  simpleDataRows,
  SimpleHeaderAndData,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import {
  DropDetailsFragment,
  DropsPageQuery,
  useDeleteDropMutation,
  useDropsPageQuery,
} from "src/components/drops/DropsPage.generated";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { createProductOfferingsUrl } from "src/RouteUrls";
import { openNewTab } from "src/utils/window";
import { DropModal } from "./DropModal";

type DropsPageViewProps = { query: ReturnType<typeof useDropsPageQuery>; documentTitleSuffix?: string };

type Row = SimpleHeaderAndData<DropDetailsFragment>;

export function DropsPageView(props: DropsPageViewProps) {
  const { query, documentTitleSuffix } = props;
  const { createDrop, editDrop, deleteDrop } = useDropActions();

  return (
    <PageHeaderLayout
      pageHeader={{ title: "Drops", documentTitleSuffix, actions: [{ label: "Add", onClick: createDrop }] }}
    >
      <GridTableLayout
        hideEditColumns
        tableProps={{
          query,
          createRows,
          columns: createColumns(editDrop, deleteDrop),
          emptyState: {
            title: "There are no Drops yet",
            actions: <Button label="Create New Drop" variant="tertiary" onClick={createDrop} />,
          },
        }}
      />
    </PageHeaderLayout>
  );
}

function useDropActions() {
  const { developmentId, projectId } = useRouteParams<{ developmentId: string; projectId: string }>();
  const { openModal } = useModal();
  const [deleteDropMutation] = useDeleteDropMutation({ refetchQueries: ["DropsPage"] });
  const { triggerNotice } = useSnackbar();

  function createDrop() {
    openModal({ content: <DropModal developmentId={developmentId} projectId={projectId} /> });
  }

  function editDrop(drop: DropDetailsFragment) {
    openModal({ content: <DropModal existingDrop={drop} developmentId={developmentId} projectId={projectId} /> });
  }

  function deleteDrop(drop: DropDetailsFragment) {
    openModal({
      content: (
        <ConfirmationModal
          confirmationMessage={
            <>
              Are you sure you want to delete this Drop{" "}
              <span css={Css.smSb.$}>
                {drop.code} {drop.name}
              </span>
              ? It will not be archived and cannot be undone.
            </>
          }
          onConfirmAction={async () => {
            await deleteDropMutation({ variables: { id: drop.id } });

            triggerNotice({
              message: (
                <span>
                  Drop changes to product offerings can be accepted on the &nbsp;
                  <span css={Css.tdu.$} onClick={() => openNewTab(createProductOfferingsUrl(developmentId, projectId))}>
                    Offerings page
                  </span>
                  &nbsp; now.
                </span>
              ),
            });
          }}
          title="Delete Drop"
          label="Yes, Delete"
          danger={true}
        />
      ),
    });
  }

  return { createDrop, editDrop, deleteDrop };
}

function createColumns(
  editDrop: (drop: DropDetailsFragment) => void,
  deleteDrop: (drop: DropDetailsFragment) => void,
): GridColumn<Row>[] {
  return [
    { header: "Drop Code", data: ({ code }) => code, mw: "120px" },
    { header: "Drop Name", data: ({ name }) => name, mw: "240px" },
    { header: "Cost Code", data: ({ costCode }) => costCode.displayName, mw: "200px" },
    { header: "Drop Task", data: ({ globalPlanTask }) => globalPlanTask.nameForEntity, mw: "240px" },
    {
      header: "Locations",
      data: ({ locations }) => (
        <div css={Css.oh.add("textOverflow", "ellipsis").onHover.ov.$}>{locations.map((l) => l.name).join(", ")}</div>
      ),
      mw: "240px",
    },
    {
      header: "Edit",
      data: (drop) => (
        <div css={Css.df.gap1.$}>
          <IconButton icon="pencil" onClick={() => editDrop(drop)} />
          <IconButton icon="trash" onClick={() => deleteDrop(drop)} />
        </div>
      ),
      w: "100px",
    },
  ];
}

function createRows(data: DropsPageQuery | undefined) {
  return simpleDataRows(data?.drops ?? []);
}
